import { Box } from '@zoopla-eng/zds-components/box';
import { Button, ButtonLeft } from '@zoopla-eng/zds-components/button';
import { ControlledDialog } from '@zoopla-eng/zds-components/dialog';
import { Divider } from '@zoopla-eng/zds-components/divider';
import { FiltersMedium } from '@zoopla-eng/zds-components/icons';
import { fontGroups } from '@zoopla-eng/zds-components/themes/font-groups';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { Added, AddedToSite } from './AddedToSite';
import { Availability, AvailableFrom } from './Availability';
import { Bedrooms, BedsMax, BedsMin } from './Bedrooms';
import { Features } from './Features';
import { labelStyle } from './Filters.styles.css';
import { FurnishedState, Furnishing } from './Furnishing';
import { Keywords } from './Keywords';
import { LetAgreed } from './LetAgreed';
import {
    IsRetirementHome,
    IsSharedAccommodation,
    LettingType
} from './LettingType';
import { BillsIncluded, MustHaves, PetsAllowed } from './MustHaves';
import { Price, PriceFrequency, PriceMax, PriceMin } from './Price';
import { PropertySubType, PropertyType } from './PropertyType';
import { trackFilterClick } from './utils/';
import {
    trackFilterClose,
    trackFilterOpen,
    trackFilterUpdate
} from './utils/filters.tracking';
import {
    getDefaultToRentFilters,
    getToRentFilters
} from './utils/getToRentFilters';

const getSearchPath = (
    propertyType: PropertyType | 'retirement' | 'bungalow',
    searchPath: string | string[]
) => {
    const searchPaths = {
        property: 'property',
        houses: 'houses',
        flats: 'flats',
        farms_land: 'property',
        retirement: 'retirement',
        bungalow: 'bungalows'
    };

    if (!searchPath || typeof searchPath === 'string') {
        return [searchPaths[propertyType]];
    }

    // there is a bug in LSRP and perhaps Next itself where pushing to the router (and triggering GIP) causes the url params to appear as a path part in router.query['search-path'] i.e ['property', 'london', '?q=london']
    const validSearchPath = searchPath.filter(
        (v) => !v.startsWith('?') && v !== '_static' && v !== 'v1' && v !== 'v2'
    );

    return [searchPaths[propertyType], ...validSearchPath.slice(1)];
};

export const ToRentFilters = ({
    container
}: {
    container: HTMLElement | null;
}) => {
    const {
        query,
        push,
        pathname: currentPageRouteFilePath,
        isReady
    } = useRouter();

    const [formValue, updateFormValue] = useState(getToRentFilters(query));

    useEffect(() => {
        updateFormValue(getToRentFilters(query));
    }, [query]);

    const [isOpen, setIsOpen] = useState(false);

    const onSubmit = async (e: { preventDefault: () => void }) => {
        e.preventDefault();

        const updatedQuery = {
            'search-path': '', // keep typescript happy
            ...query,
            ...formValue,
            pn: ''
        };

        let updatedSearchPath = getSearchPath(
            formValue['property_type'] as PropertyType,
            query['search-path'] ?? ''
        );

        if (
            formValue['is_retirement_home'] === 'true' &&
            formValue['property_type'] === 'property'
        ) {
            delete (updatedQuery as Partial<typeof updatedQuery>)[
                'is_retirement_home'
            ];

            updatedSearchPath = getSearchPath('retirement', updatedSearchPath);
        }

        if (
            formValue['property_sub_type'].length === 1 &&
            formValue['property_sub_type'][0] === 'bungalow'
        ) {
            updatedSearchPath = getSearchPath('bungalow', updatedSearchPath);
        }

        if (formValue['property_type'] !== 'farms_land') {
            delete (updatedQuery as Partial<typeof updatedQuery>)[
                'property_type'
            ];
        }

        Object.keys(updatedQuery).forEach((key) => {
            if (updatedQuery[key as keyof typeof updatedQuery] === '') {
                delete updatedQuery[key as keyof typeof updatedQuery];
            }
        });

        // 'search-path' is the name of the dynamic route parameter and so we should not apply as a genuine query param below
        const { 'search-path': invalid, ...validParams } = updatedQuery;

        const pathname = currentPageRouteFilePath.includes('/map/')
            ? ['to-rent', 'map', ...updatedSearchPath]
            : ['to-rent', ...updatedSearchPath];

        const updatedPathname = `/${pathname.join('/')}/`;

        setIsOpen(false);

        await push({
            pathname: updatedPathname,
            query: {
                ...validParams
            }
        });
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
            onSubmit({ preventDefault: () => event.preventDefault() });
        }
    };

    return (
        <>
            <ButtonLeft
                category="secondary"
                Icon={FiltersMedium}
                onClick={() => {
                    setIsOpen(true);
                    trackFilterOpen();
                }}
                disabled={!isReady}
            >
                Filters
            </ButtonLeft>
            <ControlledDialog
                isOpen={isOpen}
                onClose={() => {
                    trackFilterClose();
                    setIsOpen(false);
                }}
                label="Filter your results"
                title="Filter your results"
                footer={
                    <Box
                        display="flex"
                        columnGap="M200"
                        justifyContent="center"
                        width="100%"
                    >
                        <Button
                            category="tertiary"
                            onClick={() => {
                                trackFilterClick();
                                updateFormValue(getDefaultToRentFilters());
                            }}
                        >
                            Reset filters
                        </Button>
                        <Button
                            category="primary"
                            onClick={(e) => {
                                trackFilterUpdate();
                                onSubmit(e);
                            }}
                        >
                            Update results
                        </Button>
                    </Box>
                }
                container={container}
                mobileVariant="bottomSheet"
                variant="sideSheetRight"
            >
                <Box
                    display="flex"
                    flexDirection="column"
                    rowGap="S400"
                    pr="S400"
                    bpM100={{ px: 'L400' }}
                >
                    <p className={`${labelStyle} ${fontGroups.ladle}`}>
                        Bedrooms
                    </p>
                    <Bedrooms
                        value={{
                            bedsMin: formValue.beds_min as BedsMin,
                            bedsMax: formValue.beds_max as BedsMax
                        }}
                        onChange={function ({
                            bedsMin,
                            bedsMax
                        }: {
                            bedsMin: string;
                            bedsMax: string;
                        }): void {
                            trackFilterClick();
                            updateFormValue((oldFormValue) => ({
                                ...oldFormValue,
                                beds_min: bedsMin,
                                beds_max: bedsMax
                            }));
                        }}
                    />
                    <Divider direction="horizontal" />
                    <p className={`${labelStyle} ${fontGroups.ladle}`}>Price</p>
                    <Price
                        value={{
                            priceMin: formValue.price_min as PriceMin,
                            priceMax: formValue.price_max as PriceMax,
                            priceFrequency: (formValue.price_frequency ||
                                'per_month') as PriceFrequency
                        }}
                        onChange={({ priceMin, priceMax, priceFrequency }) => {
                            trackFilterClick();
                            updateFormValue((oldFormValue) => ({
                                ...oldFormValue,
                                price_min: priceMin,
                                price_max: priceMax,
                                price_frequency: priceFrequency
                            }));
                        }}
                    />
                    <Divider direction="horizontal" />
                    <p className={`${labelStyle} ${fontGroups.ladle}`}>
                        Property type
                    </p>
                    <PropertyType
                        value={{
                            propertyType:
                                formValue.property_type as PropertyType,
                            propertySubType:
                                formValue.property_sub_type as PropertySubType
                        }}
                        onChange={({ propertyType, propertySubType }) => {
                            trackFilterClick();
                            updateFormValue((oldFormValue) => ({
                                ...oldFormValue,
                                property_type: propertyType,
                                property_sub_type: propertySubType
                            }));
                        }}
                    />
                    <Divider direction="horizontal" />
                    <p className={`${labelStyle} ${fontGroups.ladle}`}>
                        Include, exclude & show only
                    </p>
                    <LettingType
                        value={{
                            isRetirementHome:
                                formValue.is_retirement_home as IsRetirementHome,
                            isSharedAccommodation:
                                formValue.is_shared_accommodation as IsSharedAccommodation
                        }}
                        onChange={({
                            isRetirementHome,
                            isSharedAccommodation
                        }) => {
                            trackFilterClick();
                            updateFormValue((oldFormValue) => ({
                                ...oldFormValue,
                                is_retirement_home: isRetirementHome,
                                is_shared_accommodation: isSharedAccommodation
                            }));
                        }}
                    />
                    <Divider direction="horizontal" />
                    <p className={`${labelStyle} ${fontGroups.ladle}`}>
                        Must-haves
                    </p>
                    <MustHaves
                        value={{
                            billsIncluded:
                                formValue.bills_included as BillsIncluded,
                            petsAllowed: formValue.pets_allowed as PetsAllowed,
                            feature: formValue.feature as Features
                        }}
                        onChange={({ billsIncluded, petsAllowed, feature }) => {
                            trackFilterClick();
                            updateFormValue((oldFormValue) => ({
                                ...oldFormValue,
                                bills_included: billsIncluded,
                                pets_allowed: petsAllowed,
                                feature: feature
                            }));
                        }}
                    />
                    <Divider direction="horizontal" />
                    <Furnishing
                        value={formValue.furnished_state as FurnishedState}
                        onChange={(furnishedState: FurnishedState) => {
                            trackFilterClick();
                            updateFormValue((oldFormValue) => ({
                                ...oldFormValue,
                                furnished_state: furnishedState
                            }));
                        }}
                    />
                    <Divider direction="horizontal" />
                    <Availability
                        value={formValue.available_from as AvailableFrom}
                        onChange={(availableFrom: AvailableFrom) => {
                            trackFilterClick();
                            updateFormValue((oldFormValue) => ({
                                ...oldFormValue,
                                available_from: availableFrom
                            }));
                        }}
                    />
                    <Divider direction="horizontal" />
                    <AddedToSite
                        value={formValue.added as Added}
                        onChange={(added: Added) => {
                            trackFilterClick();
                            updateFormValue((oldFormValue) => ({
                                ...oldFormValue,
                                added: added
                            }));
                        }}
                    />
                    <Divider direction="horizontal" />
                    <Keywords
                        value={formValue.keywords as Keywords}
                        onChange={(keywords) => {
                            trackFilterClick();
                            updateFormValue((oldFormValue) => ({
                                ...oldFormValue,
                                keywords: keywords
                            }));
                        }}
                        onKeyDown={handleKeyDown}
                    />
                    <Divider direction="horizontal" />
                    <LetAgreed
                        on={formValue['include_rented'] === 'true'}
                        onCheckedChange={(include_rented) => {
                            trackFilterClick();
                            updateFormValue((oldFormValue) => ({
                                ...oldFormValue,
                                include_rented
                            }));
                        }}
                    />
                </Box>
            </ControlledDialog>
        </>
    );
};
